import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import cashRegisterService from '../services/cashRegisterService';
import sessionRecorder from '../utils/sessionRecorder';
import lockoutService from '../services/lockoutService';
import { generateTravailleurId } from '../utils/frenchIdGenerator';
import { getCurrentBusinessId, normalizeBusinessStorageId } from '../utils/sharedCore';
import isoStandardIdGenerator from '../utils/ISOStandardIdGenerator';
import { emitCashierGreeting } from '../utils/cashierGreeting';
import { kvGet, kvSet, kvRemove, kvKeysStartingWith } from '../infrastructure/storage/kvAccess';
import {
  loadWorkersForBusiness,
  persistWorkersForBusiness,
  migrateWorkersExpensesPermission,
  mergeWorkersLists,
  removeAllWorkersKeysForBusiness,
  resolveWorkersBusinessId
} from '../utils/workersStorage';
import {
  loadRoleForBusiness,
  persistRoleForBusiness,
  roleStorageKey
} from '../utils/roleStorage';
import {
  pruneStaleWorkerSessions,
  reconcileAbandonedWorkerSessions
} from '../utils/workerSessionSanitizer';
import { getWorkerDraftBlock } from '../utils/workerDraftGuard';
import { hasOpenCashSessionForWorker } from '../utils/cashSessionResume';
import { sessionGet, sessionRemove } from '../infrastructure/storage/sessionAccess';
import { PENDING_WORKER_CASH_LOGIN_KEY } from '../services/workerLoginService';
import {
  markRolePinUnlocked,
  clearRolePinSession,
  isRolePinSessionActive
} from '../utils/rolePinSession';
import {
  markBusinessLogoutIntent,
  isBusinessLogoutInProgress
} from '../utils/businessLogoutFlag';
import {
  enrichCurrentBusinessFromOfflineStore,
  resolveLocalManagerCode,
  shouldSyncManagerCodeWithSupabase,
  isManagerCodeValid,
  isManagerCodeValidAsync
} from '../utils/localManagerCode';
import {
  DEFAULT_WORKER_PERMISSIONS,
  normalizeWorkerPermissions,
  hasPagePermission,
  WORKER_PAGE_PERMISSION_KEYS
} from '../utils/workerPermissions';
import { getFirstAllowedAppPath } from '../routing/appRouteCatalog';
import { useBusinessMeta } from './dataContextSlices.js';
import { warmLandingForRole } from '../utils/navRoutePrefetch';
import { markBootGate, measureBoot } from '../infrastructure/telemetry/bootTelemetry';
import { runStartupRoleMaintenance } from '../services/startupRoleMaintenance';
import { isDeferRoleMaintenanceEnabled } from '../infrastructure/startup/bootFeatureFlags';
import {
  markRoleMaintenanceDone,
  isFinancialOpsReady,
  subscribeFinancialOpsReady
} from '../infrastructure/startup/startupIntegritySignals';
import {
  getRoleIdleTimeoutMs,
  startRoleIdleWatcher
} from '../services/roleIdleLockService';

const RoleSessionContext = createContext(null);
const RoleModalContext = createContext(null);

/** Session: role, workers, permissions — stable when PIN/caisse modals toggle. */
export const useRole = () => {
  const context = useContext(RoleSessionContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

/** Modal UI: PIN / caisse / worker-edit flags — only Layout + modal hosts. */
export const useRoleModal = () => {
  const context = useContext(RoleModalContext);
  if (!context) {
    throw new Error('useRoleModal must be used within a RoleProvider');
  }
  return context;
};

function scheduleStartupIdle(task, timeoutMs = 3000) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => void task(), { timeout: timeoutMs });
  } else {
    setTimeout(() => void task(), 0);
  }
}

/**
 * The restored role is not granted to the UI (PIN is always required after a restart),
 * but startup maintenance needs it: a worker whose session is still open must not be
 * reconciled as abandoned. A deactivated worker is dropped so the session does close.
 */
function resolveRestoredRoleForMaintenance(loadedRole, businessId) {
  if (!loadedRole) return null;
  if (loadedRole.type !== 'worker') return loadedRole;
  try {
    const { workers } = loadWorkersForBusiness(businessId);
    const list = Array.isArray(workers) ? workers : [];
    // Empty list ≠ "worker deleted": workers may not be hydrated yet. False-null here
    // would wipe a PIN-valid soft-reload and mark the cashier session abandoned.
    if (list.length === 0) return loadedRole;

    const stored = list.find((w) => String(w?.id) === String(loadedRole.id));
    if (stored && stored.isActive === false) return null;
    if (stored) return loadedRole;

    // Roster missing this id — still keep if they hold the open caisse (reload race /
    // legacy id drift). Otherwise revoke.
    try {
      const open = sessionRecorder.getActiveSessions(businessId) || [];
      if (open.some((s) => String(s?.workerId) === String(loadedRole.id))) {
        return loadedRole;
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  } catch (_) {
    /* keep the role — maintenance is best-effort */
  }
  return loadedRole;
}

/** Soft-reload first paint: restore worker/manager when PIN session is still active. */
function readPinRestoredRoleSync() {
  try {
    const bid = normalizeBusinessStorageId(getCurrentBusinessId({ allowFallback: false }));
    if (!bid || !isRolePinSessionActive(bid)) return null;
    const { role: loadedRole } = loadRoleForBusiness(bid);
    return resolveRestoredRoleForMaintenance(loadedRole, bid);
  } catch {
    return null;
  }
}

export const RoleProvider = ({ children }) => {
  const deferRoleMaintenance = isDeferRoleMaintenanceEnabled();
  const navigate = useNavigate();
  const { currentBusinessType } = useBusinessMeta();
  const businessTypeRef = useRef(currentBusinessType);
  businessTypeRef.current = currentBusinessType;
  const currentRoleRef = useRef(null);

  /**
   * HashRouter-safe home navigation — goes straight to the first allowed path
   * for `roleForNav` (no `/` bounce). Pass the role explicitly at login so we
   * never wait on a stale `currentRole` closure.
   */
  const navigateToDashboard = useCallback((roleForNav) => {
    const role = roleForNav === undefined ? currentRoleRef.current : roleForNav;
    const businessType = businessTypeRef.current;
    const path = getFirstAllowedAppPath({
      businessType,
      hasPermission: (permission) => hasPagePermission(role, permission),
    });
    // Warm the exact landing chunk before navigate — Login often warmed POS only.
    warmLandingForRole({ role, businessType, path });
    navigate(path, { replace: true });
  }, [navigate]);

  const [currentRole, setCurrentRole] = useState(() => readPinRestoredRoleSync());
  const [workers, setWorkers] = useState([]);
  const [managerCode, setManagerCode] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [editingWorker, setEditingWorker] = useState(null);
  const [ready, setReady] = useState(false);
  /** Erst nach erstem Laden aus KV speichern — verhindert []-Überschreiben beim Start. */
  const workersHydratedRef = useRef(false);
  /** Nur bei explizitem clearAllWorkers leere Liste persistieren. */
  const allowEmptyWorkersSaveRef = useRef(false);
  const roleHydratedRef = useRef(false);
  const allowRemoveRoleSaveRef = useRef(false);
  const isBusinessLogoutInProgressRef = useRef(false);
  const pendingWorkerCashHandledRef = useRef(false);
  /** Kassierer-Login von der Login-Seite (Abbrechen → Accueil, nicht Code-Modal). */
  const workerLoginFromLoginPageRef = useRef(false);
  /** Login cash confirm succeeded — success screen still open; dismiss must not reopen PIN. */
  const cashRegisterLoginCompletedRef = useRef(false);
  /** Logout cash settlement succeeded — success/print screen open; dismiss → Accueil. */
  const cashRegisterLogoutCompletedRef = useRef(false);
  const showCodeModalRef = useRef(false);
  const showCashRegisterModalRef = useRef(false);
  const businessIdRef = useRef(null);

  const toCanonicalBusinessId = (id) =>
    id == null || id === '' ? null : normalizeBusinessStorageId(id);

  /**
   * Liest currentBusiness aus KV (bevorzugt) oder currentUser.business als Fallback.
   * Bereinigt bekannte korrupte Strings ([object Object]).
   * Gibt { business, businessId } zurück — beide können null sein.
   */
  const resolveCurrentBusinessData = useCallback(() => {
    let business = null;
    // 1. currentBusiness (bevorzugt, kanonisch)
    const cb = kvGet('currentBusiness');
    if (cb) {
      const isCorrupted = cb === '[object Object]' || cb === '"[object Object]"';
      if (isCorrupted) {
        console.warn('⚠️ [ROLE CONTEXT] Corrupted currentBusiness data, cleaning up');
        kvRemove('currentBusiness');
      } else if (typeof cb === 'object') {
        business = cb;
      } else {
        try { business = JSON.parse(cb); } catch { kvRemove('currentBusiness'); }
      }
    }
    // 2. currentUser.business (Fallback)
    if (!business) {
      const cu = kvGet('currentUser');
      if (cu) {
        const isCorrupted = cu === '[object Object]' || cu === '"[object Object]"';
        if (isCorrupted) {
          kvRemove('currentUser');
        } else {
          try {
            const user = typeof cu === 'object' ? cu : JSON.parse(cu);
            if (user && typeof user === 'object' && !Array.isArray(user)) {
              business = user.business || null;
            }
          } catch { /* ignore */ }
        }
      }
    }
    return {
      business: business || null,
      businessId: toCanonicalBusinessId(business?.id) || null
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadWorkersForBusiness = useCallback((explicitBusinessId) => {
    const resolved =
      toCanonicalBusinessId(explicitBusinessId) ||
      toCanonicalBusinessId(businessId) ||
      toCanonicalBusinessId(getCurrentBusinessId({ allowFallback: false }));
    if (!resolved) return;

    const { workers: loadedWorkers } = loadWorkersForBusiness(resolved);
    const migrated = migrateWorkersExpensesPermission(loadedWorkers);
    setWorkers(migrated);
    workersHydratedRef.current = true;
    setBusinessId(resolved);
  }, [businessId]);
  
  // Cash register modal state
  const [showCashRegisterModal, setShowCashRegisterModal] = useState(false);
  const [cashRegisterMode, setCashRegisterMode] = useState('login'); // 'login', 'logout', or 'pause'
  const [pendingCashRegisterAction, setPendingCashRegisterAction] = useState(null);
  const [financialOpsReady, setFinancialOpsReady] = useState(() => isFinancialOpsReady());

  showCodeModalRef.current = showCodeModal;
  showCashRegisterModalRef.current = showCashRegisterModal;
  currentRoleRef.current = currentRole;
  businessIdRef.current = businessId;

  // Do NOT close the cash-register modal while financial ops boot — that discarded
  // pendingWorkerCashLogin and dropped cashiers into the manager code modal.
  useEffect(() => subscribeFinancialOpsReady(setFinancialOpsReady), []);

  // RL10: after inactivity clear the PIN role and reopen CodeEntry (not full logout).
  useEffect(() => {
    if (!ready || !currentRole) return undefined;

    const watcher = startRoleIdleWatcher({
      timeoutMs: getRoleIdleTimeoutMs(),
      isPaused: () =>
        !currentRoleRef.current ||
        showCodeModalRef.current ||
        showCashRegisterModalRef.current ||
        isBusinessLogoutInProgressRef.current ||
        isBusinessLogoutInProgress(),
      onIdle: () => {
        if (!currentRoleRef.current) return;
        if (showCodeModalRef.current || showCashRegisterModalRef.current) return;
        if (isBusinessLogoutInProgressRef.current || isBusinessLogoutInProgress()) return;

        const bid = businessIdRef.current;
        allowRemoveRoleSaveRef.current = true;
        clearRolePinSession();
        if (bid) {
          persistRoleForBusiness(bid, null, { allowRemove: true });
        }
        setCurrentRole(null);
        setShowCodeModal(true);
      }
    });

    return () => watcher.dispose();
  }, [ready, currentRole]);

  // Load data from localStorage on mount and when business data changes
  useEffect(() => {
    const loadRoleData = async () => {
      markBootGate('role_context_start');
      try {
        // Resolve business + ID from KV in one pass
        const { business: resolvedBusiness, businessId: resolvedId } = resolveCurrentBusinessData();
        let business = resolvedBusiness;
        let currentBusinessId = resolvedId;

        // Fallback to pinned storage ID
        if (!currentBusinessId) {
          currentBusinessId = toCanonicalBusinessId(getCurrentBusinessId({ allowFallback: false }));
        }

        if (currentBusinessId && (!business || !business.manager_code)) {
          const enriched = enrichCurrentBusinessFromOfflineStore(currentBusinessId);
          if (enriched) {
            business = enriched;
            kvSet('currentBusiness', enriched);
          }
        }
        
        setBusinessId(currentBusinessId);
        
        const { role: loadedRole } = loadRoleForBusiness(currentBusinessId);
        // Soft reload (F5) keeps a sessionStorage PIN unlock so CodeEntry is not
        // forced every refresh. True cold start / idle lock / logout clear that
        // flag — physical access after app quit still requires a PIN (RL11).
        // The stored role is always passed to maintenance so an open cashier
        // session is not reconciled away as abandoned (RL13).
        const activeRole = resolveRestoredRoleForMaintenance(loadedRole, currentBusinessId);
        const pinSessionActive = isRolePinSessionActive(currentBusinessId);
        const restoreRole = Boolean(pinSessionActive && activeRole);
        if (restoreRole) {
          setCurrentRole(activeRole);
          setShowCodeModal(false);
        } else {
          if (loadedRole) {
            allowRemoveRoleSaveRef.current = true;
            persistRoleForBusiness(currentBusinessId, null, { allowRemove: true });
          }
          setCurrentRole(null);
          setShowCodeModal(
            Boolean(currentBusinessId) &&
              !isBusinessLogoutInProgress() &&
              !sessionGet(PENDING_WORKER_CASH_LOGIN_KEY)
          );
        }
        roleHydratedRef.current = true;

        // Load workers (kanonischer Key + Legacy-Keys, keine kvRemove bei Parse-Fehlern)
        const { workers: loadedWorkers, migrated: workersMigrated } =
          loadWorkersForBusiness(currentBusinessId);
        const migratedWorkers = migrateWorkersExpensesPermission(loadedWorkers);
          setWorkers(migratedWorkers);
        workersHydratedRef.current = true;

        const localManagerCode = resolveLocalManagerCode(currentBusinessId);
        if (localManagerCode) {
          setManagerCode(localManagerCode);
          kvSet('managerCode', localManagerCode);
          if (business && !business.manager_code) {
            const updatedBusiness = { ...business, manager_code: localManagerCode };
            kvSet('currentBusiness', updatedBusiness);
            business = updatedBusiness;
          }
        } else if (business?.manager_code) {
          setManagerCode(business.manager_code);
          kvSet('managerCode', business.manager_code);
        }

        const runMaintenance = async () => {
          const result = await runStartupRoleMaintenance({
            businessId: currentBusinessId,
            activeRole,
            business
          });
          if (Array.isArray(result.workers)) {
            setWorkers(result.workers);
          }
          if (result.managerCode) {
            setManagerCode(result.managerCode);
          }
        };

        if (deferRoleMaintenance) {
          scheduleStartupIdle(async () => {
            try {
              await runMaintenance();
            } catch (maintenanceError) {
              console.error('[RoleContext] Deferred role maintenance failed:', maintenanceError);
            } finally {
              markRoleMaintenanceDone();
            }
          });
        } else {
          markBootGate('role_maintenance_start');
          if (currentBusinessId) {
            const pruneResult = pruneStaleWorkerSessions(currentBusinessId);
            if (pruneResult.pruned > 0) {
              console.info(
                '[RoleContext] Stale worker sessions pruned on load:',
                pruneResult.details
              );
            }
            if (!activeRole || activeRole.type === 'manager') {
              const reconcileResult = sessionRecorder.reconcileAbandonedWorkerSessions(
                currentBusinessId
              );
              if (reconcileResult.closed > 0) {
                console.info(
                  '[RoleContext] Abandoned worker sessions closed on load:',
                  reconcileResult.details
                );
              }
            } else if (activeRole.type === 'worker') {
              sessionRecorder.reconcileAbandonedWorkerSessions(currentBusinessId, activeRole.id);
            }
          }

          const needsExpenseMigration = migratedWorkers.some(
            (w, i) => w.permissions?.expenses !== loadedWorkers[i]?.permissions?.expenses
          );
          if (workersMigrated || needsExpenseMigration) {
            void persistWorkersForBusiness(currentBusinessId, migratedWorkers, {
              allowEmpty: migratedWorkers.length === 0
            });
          }

          if (!localManagerCode) {
            try {
              if (shouldSyncManagerCodeWithSupabase(business)) {
                const { supabase } = await import('../config/supabase');
                const { data, error } = await supabase
                  .from('businesses')
                  .select('manager_code')
                  .eq('id', business.id)
                  .single();

                if (error) {
                  if (process.env.NODE_ENV === 'development') {
                    console.warn(
                      'Manager code from Supabase unavailable:',
                      error.message || error
                    );
                  }
                } else if (data?.manager_code) {
                  setManagerCode(data.manager_code);
                  kvSet('managerCode', data.manager_code);
                  const updatedBusiness = { ...business, manager_code: data.manager_code };
                  kvSet('currentBusiness', updatedBusiness);
                  const currentUser = kvGet('currentUser');
                  if (currentUser && typeof currentUser === 'object') {
                    kvSet('currentUser', { ...currentUser, business: updatedBusiness });
                  }
                }
              }
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[RoleContext] Manager code load skipped:', error?.message || error);
              }
            }
          }

          markBootGate('role_maintenance_done');
          measureBoot('role_maintenance_start', 'role_maintenance_done', 'role_maintenance');
          markRoleMaintenanceDone();
        }

      } catch (error) {
        console.error('Error loading role data:', error);
      } finally {
        // Mark role context as ready
        markBootGate('role_context_ready');
        measureBoot('role_context_start', 'role_context_ready', 'role_context_init');
        setReady(true);
      }
    };

    loadRoleData();
  }, []);

  // Listen for changes to business data in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      // Resolve business + ID from KV in one pass (shared helper, no duplication)
      const { business, businessId: newBusinessId } = resolveCurrentBusinessData();
      
      // If business ID changed, reload workers and role for new business
      if (newBusinessId && newBusinessId !== businessId) {

        setBusinessId(newBusinessId);
        reloadWorkersForBusiness(newBusinessId);

        const pruneResult = pruneStaleWorkerSessions(newBusinessId);
        if (pruneResult.pruned > 0) {
          console.info(
            '[RoleContext] Stale worker sessions pruned on business switch:',
            pruneResult.details
          );
        }
        
        // Same PIN-scoped restore as loadRoleData — never unlock a foreign
        // business role without an active pin session for that business (RPI-01).
        const { role: reloadedRole } = loadRoleForBusiness(newBusinessId);
        const activeRole = resolveRestoredRoleForMaintenance(reloadedRole, newBusinessId);
        const pinSessionActive = isRolePinSessionActive(newBusinessId);
        const restoreRole = Boolean(pinSessionActive && activeRole);
        if (restoreRole) {
          setCurrentRole(activeRole);
          setShowCodeModal(false);
        } else {
          if (reloadedRole) {
            allowRemoveRoleSaveRef.current = true;
            persistRoleForBusiness(newBusinessId, null, { allowRemove: true });
          }
          setCurrentRole(null);
          setShowCodeModal(
            Boolean(newBusinessId) &&
              !isBusinessLogoutInProgress() &&
              !sessionGet(PENDING_WORKER_CASH_LOGIN_KEY)
          );
        }
        roleHydratedRef.current = true;
      }
      
      if (business && business.manager_code && business.manager_code !== managerCode) {
        setManagerCode(business.manager_code);
        kvSet('managerCode', business.manager_code);
      }
    };

    // Check immediately
    handleStorageChange();

    // Cross-tab KV changes
    window.addEventListener('storage', handleStorageChange);
    // Same-tab business/auth updates
    window.addEventListener('authStateChanged', handleStorageChange);
    // Only business-identity events — sales/inventory/cache must not trigger KV
    // resolve on every mutation (PERF-06).
    const ROLE_STORAGE_DOMAIN_TYPES = new Set([
      'business_payload_updated',
      'business_logo_updated',
      'data_changed'
    ]);
    const onDomainEvent = (event) => {
      const type = event?.detail?.type;
      if (!type || !ROLE_STORAGE_DOMAIN_TYPES.has(type)) return;
      handleStorageChange();
    };
    window.addEventListener('elkheir:domain-event', onDomainEvent);

    // Fallback for async KV hydration: reduced frequency, skip when tab hidden.
    // Events (authStateChanged, filtered domain) cover live changes — this
    // is only a safety net for rare async KV writes that fire no event.
    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      handleStorageChange();
    }, 30000); // Was 5000 — reduced 6× to cut KV reads (events cover live changes)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleStorageChange();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleStorageChange);
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [managerCode, businessId, reloadWorkersForBusiness]);

  // Nach Business-Login (Manager/Kassierer) Employés aus KV neu laden.
  // Wichtig: stille Session-Updates (cloud_plan, sync) dürfen CodeEntry NICHT erneut öffnen.
  useEffect(() => {
    const onAuthChanged = (event) => {
      const user = event?.detail?.user;
      if (user?.business?.id) {
        const bid = toCanonicalBusinessId(user.business.id);
        const prevBid = businessIdRef.current
          ? toCanonicalBusinessId(businessIdRef.current)
          : null;
        const sameBusiness = Boolean(prevBid && bid && prevBid === bid);

        setBusinessId(bid);
        if (user.business.manager_code) {
          setManagerCode(String(user.business.manager_code));
        }

        // Cashier handoff owns the role
        if (sessionGet(PENDING_WORKER_CASH_LOGIN_KEY)) {
          setShowCodeModal(false);
          return;
        }

        // Silent metadata refresh (plan, cloudId, subscription) — keep PIN unlocked
        if (
          sameBusiness &&
          (currentRoleRef.current || isRolePinSessionActive(bid))
        ) {
          // Workers rarely change via session payload; avoid full PIN reset
          return;
        }

        // Real login / business switch → require CodeEntry
        pendingWorkerCashHandledRef.current = false;
        reloadWorkersForBusiness(bid);
        clearRolePinSession();
        allowRemoveRoleSaveRef.current = true;
        persistRoleForBusiness(bid, null, { allowRemove: true });
        setCurrentRole(null);
        setShowCodeModal(true);
        roleHydratedRef.current = true;
        return;
      }
      if (!user) {
        pendingWorkerCashHandledRef.current = false;
        clearRolePinSession();
        setCurrentRole(null);
        setShowCodeModal(false);
      }
    };

    window.addEventListener('authStateChanged', onAuthChanged);
    return () => window.removeEventListener('authStateChanged', onAuthChanged);
  }, [reloadWorkersForBusiness]);

  // Ohne Rolle: Code-Modal — nur innerhalb einer aktiven Business-Session.
  // Nie während Kassen-Modal / Business-Logout → sonst landet man auf Manager-Code statt Login.
  useEffect(() => {
    if (!ready || !roleHydratedRef.current) return;
    if (isBusinessLogoutInProgressRef.current) return;
    if (showCashRegisterModal) return;
    if (sessionGet(PENDING_WORKER_CASH_LOGIN_KEY)) return;

    if (currentRole != null) return;

    if (isBusinessLogoutInProgress()) {
      setShowCodeModal(false);
      return;
    }

    const hasBusinessSession = Boolean(kvGet('currentUser') || kvGet('currentBusiness'));
    if (!hasBusinessSession) {
      setShowCodeModal(false);
      return;
    }

    setShowCodeModal(true);
  }, [currentRole, ready, showCashRegisterModal]);

  // Kassierer-Login von der Login-Seite → Kassen-Modal
  useEffect(() => {
    if (!ready || pendingWorkerCashHandledRef.current) return;
    const pending = sessionGet(PENDING_WORKER_CASH_LOGIN_KEY);
    if (!pending || typeof pending !== 'object') return;

    // Open cash modal immediately — do not wait on financialOpsReady (that mixed cashier into manager CodeEntry)
    const resolvedBusinessId =
      toCanonicalBusinessId(businessId) ||
      toCanonicalBusinessId(getCurrentBusinessId({ allowFallback: false }));
    if (!resolvedBusinessId) return;

    pendingWorkerCashHandledRef.current = true;
    sessionRemove(PENDING_WORKER_CASH_LOGIN_KEY);

    const workerFromList = workers.find((w) => String(w.id) === String(pending.workerId));
    const workerRole = {
      type: 'worker',
      id: pending.workerId,
      name: pending.workerName || workerFromList?.name || 'Worker',
      code: pending.workerCode || workerFromList?.code || '',
      permissions: normalizeWorkerPermissions(
        pending.permissions || workerFromList?.permissions || DEFAULT_WORKER_PERMISSIONS
      )
    };

    // Clear any leftover manager role so persist cannot resurrect it during handoff.
    allowRemoveRoleSaveRef.current = true;
    setCurrentRole(null);
    setShowCodeModal(false);

    if (pending.resumedFromPause) {
      sessionRecorder.resumeCashRegister(resolvedBusinessId);
      setCurrentRole(workerRole);
      markRolePinUnlocked(resolvedBusinessId);
      setShowCashRegisterModal(false);
      setPendingCashRegisterAction(null);
      navigateToDashboard(workerRole);
      emitCashierGreeting('resume', { workerName: workerRole.name });
      return;
    }

    const previousAmount = null;

    sessionRecorder.reconcileCashRegisterLockState(resolvedBusinessId);
    reconcileAbandonedWorkerSessions(resolvedBusinessId, workerRole.id);
    pruneStaleWorkerSessions(resolvedBusinessId);

    workerLoginFromLoginPageRef.current = true;
    cashRegisterLoginCompletedRef.current = false;
    setCashRegisterMode('login');
    setPendingCashRegisterAction({ type: 'login', workerRole, previousAmount });
    setShowCashRegisterModal(true);
  }, [ready, businessId, workers]);

  // Workers persist when they or businessId change (after initial hydration).

  // Save data to localStorage when it changes
  useEffect(() => {
    if (!ready) return;
    if (!roleHydratedRef.current && currentRole == null) return;

    const allowRemove = allowRemoveRoleSaveRef.current;
    const result = persistRoleForBusiness(businessId, currentRole, { allowRemove });
    if (result.skipped) {
      if (isBusinessLogoutInProgressRef.current) return;
      console.warn(
        '⚠️ [ROLE CONTEXT] Skipped removing role (existing session preserved):',
        result.reason
      );
      const { role: recovered } = loadRoleForBusiness(businessId);
      if (recovered) setCurrentRole(recovered);
      return;
    }
    if (allowRemove) allowRemoveRoleSaveRef.current = false;
    if (currentRole && businessId) {
      markRolePinUnlocked(businessId);
    }
  }, [currentRole, businessId, ready]);

  useEffect(() => {
    if (!ready) return;
    if (!workersHydratedRef.current && workers.length === 0) return;

    const uniqueWorkers = mergeWorkersLists(workers);

    if (uniqueWorkers.length !== workers.length) {
      setWorkers(uniqueWorkers);
      return;
    }

    const repairedIds = uniqueWorkers.some((w, i) => w.id !== workers[i]?.id);
    if (repairedIds) {
      setWorkers(uniqueWorkers);
      return;
    }

    const resolvedBusinessId = resolveWorkersBusinessId(businessId);
    if (!resolvedBusinessId && uniqueWorkers.length > 0) {
      console.warn('[RoleContext] Workers in memory but business id missing — skip persist');
      return;
    }

    const allowEmpty = allowEmptyWorkersSaveRef.current;
    void persistWorkersForBusiness(resolvedBusinessId, uniqueWorkers, { allowEmpty }).then(
      (result) => {
        if (result.skipped) {
          if (result.reason === 'would_overwrite_non_empty') {
            console.warn(
              '⚠️ [ROLE CONTEXT] Skipped saving empty workers list (existing data preserved):',
              result.reason
            );
            reloadWorkersForBusiness(resolvedBusinessId);
          }
          return;
        }
        if (allowEmpty) {
          allowEmptyWorkersSaveRef.current = false;
        }

        if (resolvedBusinessId && uniqueWorkers.length >= 0) {
          const knownIds = new Set(
            uniqueWorkers
              .filter((w) => w?.id && w.isActive !== false)
              .map((w) => String(w.id))
          );
          pruneStaleWorkerSessions(resolvedBusinessId, knownIds);
        }
      }
    );
  }, [workers, businessId, ready, reloadWorkersForBusiness]);

  useEffect(() => {
    if (!ready || !currentRole || currentRole.type !== 'worker') return;
    const fresh = workers.find((w) => String(w.id) === String(currentRole.id));
    if (!fresh) return;

    const nextPermissions = normalizeWorkerPermissions(fresh.permissions);
    const currentPermissions = normalizeWorkerPermissions(currentRole.permissions);
    const changed = WORKER_PAGE_PERMISSION_KEYS.some(
      (key) => nextPermissions[key] !== currentPermissions[key]
    );
    if (!changed) return;

    setCurrentRole((prev) =>
      prev && prev.type === 'worker' ? { ...prev, permissions: nextPermissions } : prev
    );
  }, [workers, ready, currentRole]);

  useEffect(() => {
    if (managerCode) {
      kvSet('managerCode', managerCode);
    } else {
      kvRemove('managerCode');
    }
  }, [managerCode]);

  // Generate a systematic worker ID (5-6 digits)
  const generateWorkerCode = () => {
    // Existing worker codes and the manager code are both off limits: a shared PIN
    // resolves to the worker and locks the manager out of CodeEntry (RL12).
    const takenCodes = new Set(workers.map((worker) => String(worker.code)));
    const activeManagerCode =
      resolveLocalManagerCode(businessId) || managerCode || kvGet('managerCode');
    if (activeManagerCode) takenCodes.add(String(activeManagerCode));

    // Generate 5-digit code (10000-99999)
    let attempts = 0;
    let newCode;
    
    do {
      // Start with 10000 and go up to 99999
      newCode = Math.floor(10000 + Math.random() * 90000).toString();
      attempts++;
      
      // If we've tried too many times, increase to 6 digits
      if (attempts > 100) {
        newCode = Math.floor(100000 + Math.random() * 900000).toString();
      }
    } while (takenCodes.has(newCode) && attempts < 200);
    
    return newCode;
  };

  // Generate systematic worker ID (ISO-Standard Konform)
  const generateWorkerId = () => {
    try {
      // Verwende ISO-Standard Generator für sequenzielle Worker IDs
      const isoId = isoStandardIdGenerator.generateTravailleurId();
      return isoId;
      
    } catch (error) {
      console.error('RoleContext: Error generating ISO Worker ID:', error);
      
      // Fallback: Verwende alte Methode
      const existingIds = workers.map(worker => worker.id);
      let attempts = 0;
      let newId;
      
      do {
        newId = generateTravailleurId();
        attempts++;
      } while (existingIds.includes(newId) && attempts < 200);
      
      console.warn('RoleContext: Using fallback Worker ID:', newId);
      return newId;
    }
  };

  // Migrate existing workers to new ID system
  const migrateWorkersToNewIdSystem = () => {
    const updatedWorkers = workers.map(worker => {
      // Check if worker already has new ID format (TRAV prefix)
      if (worker.id.startsWith('TRAV') && worker.id.length >= 8) {
        return worker; // Already migrated
      }
      
      // Generate new systematic ID for old workers
      const newId = generateWorkerId();
      return {
        ...worker,
        id: newId,
        createdAt: worker.createdAt || new Date().toISOString(),
        status: worker.status || 'active'
      };
    });
    
    setWorkers(updatedWorkers);
    return updatedWorkers;
  };

  const mergeWorkersSources = (storedWorkers, memoryWorkers) => {
    return mergeWorkersLists(storedWorkers, memoryWorkers);
  };

  // Add a new worker — wartet auf KV-Persistenz bevor Erfolg gemeldet wird
  // optional permissions: UI-Vorselektion beim Anlegen (sonst DEFAULT_WORKER_PERMISSIONS)
  const addWorker = async (name, permissions) => {
    const trimmedName = String(name || '').trim();
    if (!trimmedName) {
      return { success: false, error: 'empty_name' };
    }

    const resolvedId = resolveWorkersBusinessId(businessId);
    if (!resolvedId) {
      console.warn('[RoleContext] addWorker: business id missing — cannot persist');
      return { success: false, error: 'missing_business_id' };
    }

    const { workers: storedWorkers } = loadWorkersForBusiness(resolvedId);
    const baseWorkers = mergeWorkersSources(storedWorkers, workers);

    let newId;
    let newCode;
    let attempts = 0;
    const maxAttempts = 200;

    do {
      newId = generateWorkerId();
      attempts++;
      if (attempts >= maxAttempts) {
        newId = `TRAV-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`;
        break;
      }
    } while (baseWorkers.some((worker) => worker.id === newId));

    attempts = 0;
    do {
      newCode = generateWorkerCode();
      attempts++;
      if (attempts >= maxAttempts) {
        newCode = Date.now().toString().slice(-6);
        break;
      }
    } while (baseWorkers.some((worker) => worker.code === newCode));

    const resolvedPermissions = normalizeWorkerPermissions(
      permissions && typeof permissions === 'object'
        ? permissions
        : DEFAULT_WORKER_PERMISSIONS
    );

    const newWorker = {
      id: newId,
      name: trimmedName,
      code: newCode,
      permissions: resolvedPermissions,
      createdAt: new Date().toISOString(),
      isActive: true
    };

    if (
      baseWorkers.some(
        (worker) => worker.id === newWorker.id || worker.code === newWorker.code
      )
    ) {
      console.error('[RoleContext] Duplicate worker detected after generation', newWorker);
      return { success: false, error: 'duplicate' };
    }

    const next = [...baseWorkers, newWorker];

    try {
      const persistResult = await persistWorkersForBusiness(resolvedId, next);
      if (persistResult.skipped) {
        console.warn('[RoleContext] addWorker persist skipped:', persistResult.reason);
        return { success: false, error: persistResult.reason || 'persist_skipped' };
      }

      const migrated = migrateWorkersExpensesPermission(next);
      setWorkers(migrated);
      workersHydratedRef.current = true;
      setBusinessId(resolvedId);
      try {
        const { flushPendingStorageWrites } = await import(
          '../infrastructure/storage/businessPayloadWrite'
        );
        await flushPendingStorageWrites();
      } catch (flushErr) {
        console.warn('[RoleContext] addWorker flush:', flushErr?.message || flushErr);
      }
      reloadWorkersForBusiness(resolvedId);
      return { success: true, worker: newWorker };
    } catch (err) {
      console.error('[RoleContext] addWorker persist failed:', err);
      return { success: false, error: 'persist_failed' };
    }
  };

  // Remove a worker (deprecated - use toggleWorkerStatus instead)
  const removeWorker = (workerId) => {
    setWorkers(prev => prev.filter(worker => worker.id !== workerId));
    
    // Clear worker sessions when removing worker
    if (businessId) {
      sessionRecorder.clearWorkerSessions(workerId, businessId);
    }
  };

  // Toggle worker active status (deactivate/reactivate)
  const toggleWorkerStatus = (workerId) => {
    const target = workers.find((w) => w.id === workerId);
    const wasActive = target ? target.isActive !== false : true;
    const deactivating = Boolean(target) && wasActive;

    setWorkers((prev) =>
      prev.map((worker) => {
        if (worker.id !== workerId) return worker;
        const currentStatus = worker.isActive !== undefined ? worker.isActive : true;
        return { ...worker, isActive: !currentStatus };
      })
    );

    if (!deactivating) return;

    if (businessId) {
      sessionRecorder.closeActiveSessionsForWorker(workerId, businessId);
      pruneStaleWorkerSessions(businessId);
    }

    // Revoke on the spot — waiting for the next loginWithCode left the deactivated
    // cashier with full rights until logout (RL13).
    if (currentRole?.type === 'worker' && String(currentRole.id) === String(workerId)) {
      allowRemoveRoleSaveRef.current = true;
      clearRolePinSession();
      persistRoleForBusiness(businessId, null, { allowRemove: true });
      setCurrentRole(null);
      setShowCodeModal(true);
      navigateToDashboard(null);
    }
  };

  // Update worker permissions — always persist a full normalized set
  const updateWorkerPermissions = (workerId, newPermissions) => {
    setWorkers((prev) =>
      prev.map((worker) => {
        if (String(worker.id) !== String(workerId)) return worker;
        return {
          ...worker,
          permissions: normalizeWorkerPermissions({
            ...worker.permissions,
            ...newPermissions,
          }),
        };
      })
    );

    setCurrentRole((prev) => {
      if (!prev || prev.type !== 'worker' || String(prev.id) !== String(workerId)) {
        return prev;
      }
      return {
        ...prev,
        permissions: normalizeWorkerPermissions({
          ...prev.permissions,
          ...newPermissions,
        }),
      };
    });
  };

  // Login with code (works offline)
  const loginWithCode = async (code) => {
    // Check if system is locked out
    if (lockoutService.isLockedOut()) {
      const remaining = lockoutService.getRemainingLockoutTime();
      return { 
        success: false, 
        error: 'System locked out',
        message: `System is locked out. Please wait ${remaining.minutes}:${remaining.seconds.toString().padStart(2, '0')} before trying again.`,
        isLockedOut: true
      };
    }

    // First try to refresh manager code from all local sources
    const resolvedBusinessId =
      businessId || toCanonicalBusinessId(getCurrentBusinessId({ allowFallback: false }));
    refreshManagerCode();

    // Cloud: always refresh manager_code by auth session (Pages / second device).
    // ID-only lookup often fails after slug vs cloud UUID split; auth_user_id is SoT.
    let cloudManagerHashMissing = false;
    try {
      if (resolvedBusinessId) {
        try {
          const { forceAllowSupabaseNetwork } = await import('../utils/supabaseReachability');
          forceAllowSupabaseNetwork();
        } catch {
          /* optional */
        }
        const { supabase } = await import('../config/supabase');
        let remoteRow = null;

        const {
          data: { user: authUser }
        } = await supabase.auth.getUser();
        if (authUser?.id) {
          const { data } = await supabase
            .from('businesses')
            .select('id, manager_code')
            .eq('auth_user_id', authUser.id)
            .maybeSingle();
          if (data) remoteRow = data;
        }

        if (!remoteRow) {
          const { resolveSupabaseBusinessId } = await import('../utils/businessTypeCloud');
          const remoteId =
            resolveSupabaseBusinessId(resolvedBusinessId) ||
            kvGet('currentBusiness')?.cloudId ||
            resolvedBusinessId;
          const idCandidates = [
            String(remoteId || ''),
            String(resolvedBusinessId || ''),
            normalizeBusinessStorageId(resolvedBusinessId) || '',
            kvGet('currentBusiness')?.cloudId
              ? String(kvGet('currentBusiness').cloudId)
              : ''
          ].filter(Boolean);
          for (const id of [...new Set(idCandidates)]) {
            const { data } = await supabase
              .from('businesses')
              .select('id, manager_code')
              .eq('id', id)
              .maybeSingle();
            if (data) {
              remoteRow = data;
              break;
            }
          }
        }

        if (remoteRow?.manager_code) {
          setManagerCode(remoteRow.manager_code);
          kvSet('managerCode', remoteRow.manager_code);
          try {
            const { offlineAuthService } = await import('../services/offlineAuthService');
            offlineAuthService.updateBusiness(resolvedBusinessId, {
              manager_code: remoteRow.manager_code,
              cloudId: remoteRow.id || undefined
            });
          } catch {
            /* optional */
          }
          try {
            const cur = kvGet('currentBusiness');
            if (cur && typeof cur === 'object') {
              kvSet('currentBusiness', {
                ...cur,
                manager_code: remoteRow.manager_code,
                cloudId: remoteRow.id || cur.cloudId
              });
            }
          } catch {
            /* optional */
          }
        } else if (remoteRow && !remoteRow.manager_code) {
          // Trigger/signup path left manager_code NULL — Pages cannot verify old PIN
          cloudManagerHashMissing = true;
        }

        // Worker PINs from cloud field snapshot (hybrid/mirror) if empty locally
        if (!Array.isArray(workers) || workers.length === 0) {
          try {
            const { pullFieldSnapshotsFromCloud } = await import('../services/parallelCloudFieldSync');
            await pullFieldSnapshotsFromCloud(resolvedBusinessId);
            reloadWorkersForBusiness(resolvedBusinessId);
          } catch {
            /* optional */
          }
        }
      }
    } catch {
      /* cloud PIN pull optional */
    }

    const currentManagerCode = resolveLocalManagerCode(resolvedBusinessId);
    const { credentialsMatch } = await import('../utils/authCredentialRead');
    // Prefer an active worker match before manager — shared PINs previously always opened Manager.
    const fromKv = loadWorkersForBusiness(resolvedBusinessId).workers || [];
    const liveWorkers = fromKv.length > 0 ? fromKv : Array.isArray(workers) ? workers : [];
    const worker = liveWorkers.find((w) => credentialsMatch(w.code, code));
    let managerCodeMatches = await isManagerCodeValidAsync(code, resolvedBusinessId);

    // Pages cloud: manager_code never written → first PIN after owner login defines it
    if (!managerCodeMatches && !worker && cloudManagerHashMissing) {
      try {
        const setRes = await setManagerCodeAndSave(String(code).trim());
        if (setRes?.success) {
          managerCodeMatches = true;
          console.info(
            '[RoleContext] manager_code was NULL in cloud — saved first PIN from CodeEntry'
          );
        }
      } catch (bootstrapErr) {
        console.warn(
          '[RoleContext] manager PIN bootstrap failed:',
          bootstrapErr?.message || bootstrapErr
        );
      }
    }
    
    if (worker && worker.isActive !== false) {
      // Cashiers: only on the POS machine that holds business machine_id (fail closed)
      try {
        const sessionBiz = kvGet('currentBusiness') || kvGet('currentUser')?.business || null;
        const { machineBindingService } = await import('../services/machineBindingService');
        const bid = resolvedBusinessId || sessionBiz?.id || sessionBiz?.cloudId;
        if (bid) {
          const validation = await machineBindingService.validateMachineId(bid);
          if (!validation.canProceed) {
            lockoutService.recordFailedAttempt();
            return {
              success: false,
              error: 'machine_binding',
              message:
                validation.reason ||
                'Connexion caissier uniquement sur le PC de caisse installé.'
            };
          }
          // First cashier PIN on still-unbound POS: establish bind (manager never steals it remotely)
          if (validation.isFirstLogin) {
            const bind = await machineBindingService.bindBusinessToThisMachine(bid, {
              allowRebind: false,
              business: sessionBiz
            });
            if (!bind.success) {
              lockoutService.recordFailedAttempt();
              return {
                success: false,
                error: 'machine_binding',
                message: bind.reason || 'Lien appareil impossible.'
              };
            }
          }
        }
      } catch (bindErr) {
        console.warn('[RoleContext] machine check on worker PIN:', bindErr?.message || bindErr);
        lockoutService.recordFailedAttempt();
        return {
          success: false,
          error: 'machine_binding',
          message:
            'Vérification appareil échouée — connexion caissier refusée sur ce PC.'
        };
      }

      const workerRole = {
        type: 'worker',
        name: worker.name,
        code: worker.code,
        permissions: normalizeWorkerPermissions(worker.permissions),
        id: worker.id
      };
      
      if (resolvedBusinessId) {
        pruneStaleWorkerSessions(resolvedBusinessId);
      }

      const pauseStatus = resolvedBusinessId
        ? sessionRecorder.isCashRegisterPaused(resolvedBusinessId)
        : null;
      if (pauseStatus) {
        if (String(pauseStatus.workerId) !== String(worker.id)) {
          const storedLanguage = kvGet('appLanguage') || kvGet('currentLanguage') || 'fr';
          return {
            success: false,
            error: 'Cash register paused',
            message:
              storedLanguage === 'ar'
                ? 'الصندوق متوقف مؤقتاً. فقط أمين الصندوق الذي أوقفه يمكنه الاستئناف برمزه — لا يمكن لأي كاشير آخر الدخول.'
                : 'Caisse en pause. Seul le caissier qui l\'a mise en pause peut reprendre avec son code — aucun autre caissier ne peut se connecter.'
          };
        }

        sessionRecorder.resumeCashRegister(resolvedBusinessId);
        setCurrentRole(workerRole);
        if (resolvedBusinessId) markRolePinUnlocked(resolvedBusinessId);
        setShowCodeModal(false);
        setShowCashRegisterModal(false);
        setPendingCashRegisterAction(null);
        navigateToDashboard(workerRole);
        emitCashierGreeting('resume', { workerName: workerRole.name });
        return { success: true, role: workerRole, resumedFromPause: true };
      }

      // Soft-reload / re-PIN: open caisse for this cashier already exists —
      // do NOT force the full login+écart cash modal again (restaurant + magasin).
      if (resolvedBusinessId) {
        sessionRecorder.reconcileCashRegisterLockState(resolvedBusinessId);
        const activeSessions = sessionRecorder.getActiveSessions(resolvedBusinessId) || [];
        const selfOpen = hasOpenCashSessionForWorker(activeSessions, worker.id);
        if (selfOpen) {
          reconcileAbandonedWorkerSessions(resolvedBusinessId, worker.id);
          setCurrentRole(workerRole);
          markRolePinUnlocked(resolvedBusinessId);
          setShowCodeModal(false);
          setShowCashRegisterModal(false);
          setPendingCashRegisterAction(null);
          navigateToDashboard(workerRole);
          emitCashierGreeting('welcome', { workerName: workerRole.name });
          return { success: true, role: workerRole, resumedOpenSession: true };
        }

        if (sessionRecorder.isCashRegisterLocked(resolvedBusinessId, worker.id)) {
          const storedLanguage = kvGet('appLanguage') || kvGet('currentLanguage') || 'fr';
          return {
            success: false,
            error: 'Cash register locked',
            message:
              storedLanguage === 'ar'
                ? 'الصندوق قيد الاستخدام من قبل كاشير آخر.'
                : 'Caisse actuellement en service par un autre caissier.'
          };
        }

        reconcileAbandonedWorkerSessions(resolvedBusinessId, worker.id);
        pruneStaleWorkerSessions(resolvedBusinessId);
      }

      // Fresh cash open — show opening-amount confirmation once
      workerLoginFromLoginPageRef.current = false;
      cashRegisterLoginCompletedRef.current = false;
      const previousAmount = null;
      setCashRegisterMode('login');
      setPendingCashRegisterAction({ type: 'login', workerRole, previousAmount });
      setShowCashRegisterModal(true);
      setShowCodeModal(false);

      // Pre-position URL on first allowed page while cash modal is open (no `/` bounce later).
      navigateToDashboard(workerRole);

      return { success: true, role: workerRole, requiresCashConfirmation: true };
    }

    // Manager code — only if no active worker owns this PIN
    if (managerCodeMatches) {
      const matchedCode = currentManagerCode || String(code).trim();
      setManagerCode(matchedCode);
      kvSet('managerCode', matchedCode);

      // Never resume / steal a cashier pause or force écart from manager PIN.
      // Active caisse stays with the cashier until they logout/pause themselves.
      if (resolvedBusinessId) {
        const ncrActive = cashRegisterService.getActiveSession(resolvedBusinessId);
        const keepId =
          ncrActive && !ncrActive.logoutTime && ncrActive.workerId != null
            ? ncrActive.workerId
            : null;
        reconcileAbandonedWorkerSessions(resolvedBusinessId, keepId);
      }

      const managerRole = {
        type: 'manager',
        name: 'Manager',
        code: matchedCode
      };
      setCurrentRole(managerRole);
      if (resolvedBusinessId) markRolePinUnlocked(resolvedBusinessId);
      if (resolvedBusinessId) {
        void (async () => {
          try {
            const { refreshDiscrepancyManagementReadCache, clearDiscrepancyManagementReadCache } =
              await import('../utils/discrepancyManagementReadSource');
            const { dispatchSyncBusinessFieldsFromKv } = await import('../utils/businessStateSync');
            clearDiscrepancyManagementReadCache(resolvedBusinessId);
            await refreshDiscrepancyManagementReadCache(resolvedBusinessId);
            dispatchSyncBusinessFieldsFromKv(resolvedBusinessId, ['discrepancyManagement']);
          } catch (ecartErr) {
            console.warn('[RoleContext] ecart refresh on manager login:', ecartErr?.message || ecartErr);
          }
        })();
      }
      setShowCodeModal(false);
      setShowCashRegisterModal(false);
      setPendingCashRegisterAction(null);
      navigateToDashboard(managerRole);

      return { success: true, role: managerRole };
    }

    if (worker && worker.isActive === false) {
      const storedLanguage = kvGet('appLanguage') || 'fr';
      const lockoutResult = lockoutService.recordFailedAttempt();
      return {
        success: false,
        error: 'Worker deactivated',
        message:
          storedLanguage === 'ar'
            ? 'هذا الموظف غير نشط. يرجى الاتصال بالمدير.'
            : 'Ce travailleur est désactivé. Veuillez contacter le gestionnaire.',
        isDeactivated: true,
        attemptsRemaining: lockoutService.MAX_ATTEMPTS - lockoutResult.attempts,
        isLockedOut: lockoutResult.isLockedOut
      };
    }

    // Invalid code - record failed attempt
    const lockoutResult = lockoutService.recordFailedAttempt();
    const remainingAttempts = lockoutService.MAX_ATTEMPTS - lockoutResult.attempts;
    
    let errorMessage = '';
    if (lockoutResult.isLockedOut) {
      errorMessage = 'Too many failed attempts! System locked out for 15 minutes.';
    } else if (remainingAttempts > 0) {
      errorMessage = `Invalid code. ${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} remaining.`;
    } else {
      errorMessage = 'Invalid code.';
    }

    return { 
      success: false, 
      error: 'Invalid code',
      message: errorMessage,
      attemptsRemaining: remainingAttempts,
      isLockedOut: lockoutResult.isLockedOut
    };
  };

  /** Schließt UI-Modals und beendet Business-Session → Login-Seite. */
  const finishLogoutToLoginPage = async () => {
    isBusinessLogoutInProgressRef.current = true;
    markBusinessLogoutIntent();
    pendingWorkerCashHandledRef.current = false;
    workerLoginFromLoginPageRef.current = false;
    allowRemoveRoleSaveRef.current = true;
    roleHydratedRef.current = true;
    clearRolePinSession();
    setShowCodeModal(false);
    setShowCashRegisterModal(false);
    setPendingCashRegisterAction(null);
    setCurrentRole(null);

    try {
      const { completeLogout } = await import('../services/completeLogoutService');
      // Soft path: React zeigt Login sofort (kein Full-Reload in Electron)
      await completeLogout({ reload: false });
    } catch (error) {
      console.error('Error during logout to login page:', error);
      try {
        const { clearBusinessSessionSync } = await import('../services/completeLogoutService');
        clearBusinessSessionSync();
      } catch (_) {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user: null } }));
      try {
        if (typeof window !== 'undefined' && window.electronAPI) {
          if (window.location.hash !== '#/' && window.location.hash !== '') {
            window.location.hash = '#/';
          }
        } else {
          const { redirectToLogin } = await import('../services/completeLogoutService');
          redirectToLogin({ forceReload: false });
        }
      } catch (_) {
        /* ignore */
      }
    } finally {
      // CodeEntry blockieren bis Login gemountet ist (RoleProvider unmountet mit currentUser=null).
      let frames = 0;
      const releaseLogoutGuard = () => {
        frames += 1;
        if (frames >= 3) {
          isBusinessLogoutInProgressRef.current = false;
          return;
        }
        requestAnimationFrame(releaseLogoutGuard);
      };
      requestAnimationFrame(releaseLogoutGuard);
    }
  };

  /**
   * Settle cash + optionally keep modal open for print (deferNavigation).
   * Single write path: recordLogout owns legacy + NCR close.
   */
  const recordWorkerLogoutAndExit = async (workerRole, cashAmount, confirmationType, extras = {}, options = {}) => {
    const { deferNavigation = true } = options;
    const resolvedBusinessId =
      toCanonicalBusinessId(businessId) ||
      toCanonicalBusinessId(getCurrentBusinessId({ allowFallback: false }));

    const logoutResult = await sessionRecorder.recordLogout(
      workerRole.id,
      cashAmount,
      resolvedBusinessId,
      confirmationType,
      extras.explanation || '',
      true,
      {
        electronicAmount: extras.electronicAmount,
        totalAmount: extras.totalAmount,
        amountsByProvider: extras.amountsByProvider || null
      }
    );

    if (!logoutResult || logoutResult.success === false) {
      throw new Error(logoutResult?.reason || 'recordLogout_failed');
    }

    // One cleanup pass (prune + align NCR); do not re-close or re-reconcile thrice.
    if (resolvedBusinessId) {
      try {
        sessionRecorder.reconcileCashRegisterLockState(resolvedBusinessId);
      } catch (_) {
        /* best-effort */
      }
    }

    void import('../infrastructure/storage/businessPayloadWrite')
      .then(({ flushPendingStorageWrites }) => flushPendingStorageWrites())
      .catch((flushErr) => {
        console.warn('[RoleContext] flush before logout:', flushErr?.message || flushErr);
      });

    if (deferNavigation) {
      cashRegisterLogoutCompletedRef.current = true;
      return logoutResult;
    }

    cashRegisterLogoutCompletedRef.current = false;
    await finishLogoutToLoginPage();
    return logoutResult;
  };

  const beginWorkerLogoutWithCashRegister = () => {
    if (!currentRole || currentRole.type !== 'worker') return false;

    const storedLanguage = kvGet('currentLanguage') || kvGet('appLanguage') || 'fr';
    const draftBlock = getWorkerDraftBlock(getCurrentBusinessId(), storedLanguage);
    if (draftBlock.blocked) {
      alert(draftBlock.message);
      return false;
    }

    cashRegisterLogoutCompletedRef.current = false;
    setCashRegisterMode('logout');
    setPendingCashRegisterAction({ type: 'logout', workerRole: currentRole });
    setShowCashRegisterModal(true);
    return true;
  };

  /** Abbrechen Kassen-Modal / dismiss success after clôture. */
  const cancelCashRegisterModal = () => {
    const pendingType = pendingCashRegisterAction?.type;
    const loginCompleted = cashRegisterLoginCompletedRef.current;
    const logoutCompleted = cashRegisterLogoutCompletedRef.current;
    const fromLoginPage =
      pendingType === 'login' && workerLoginFromLoginPageRef.current && !loginCompleted;

    // After counted logout + optional print → full Accueil (no duplicate role clear).
    if (pendingType === 'logout' && logoutCompleted) {
      cashRegisterLogoutCompletedRef.current = false;
      void finishLogoutToLoginPage();
      return;
    }

    setShowCashRegisterModal(false);
    setPendingCashRegisterAction(null);
    workerLoginFromLoginPageRef.current = false;
    cashRegisterLoginCompletedRef.current = false;
    cashRegisterLogoutCompletedRef.current = false;

    if (fromLoginPage) {
      void finishLogoutToLoginPage();
      return;
    }

    // Abort before login confirm → back to PIN.
    if (pendingType === 'login' && !loginCompleted) {
      setShowCodeModal(true);
    }
  };

  /**
   * Zur Login-Seite (Accueil). Worker: immer erst Clôture comptée.
   */
  const logoutToLoginPage = () => {
    if (currentRole?.type === 'worker') {
      if (beginWorkerLogoutWithCashRegister()) return;
    }
    void finishLogoutToLoginPage();
  };

  /** Déconnexion → clôture caisse (Worker) oder Accueil (Manager). */
  const logoutFromRole = () => {
    if (currentRole?.type === 'worker') {
      beginWorkerLogoutWithCashRegister();
      return;
    }
    void finishLogoutToLoginPage();
  };

  // Clear all workers (for admin purposes only)
  const clearAllWorkers = () => {
    allowEmptyWorkersSaveRef.current = true;
    workersHydratedRef.current = true;
    setWorkers([]);
    removeAllWorkersKeysForBusiness(businessId);
    kvRemove('workers');

    if (businessId) {
      sessionRecorder.clearAllBusinessSessions(businessId);
    }
  };

  // Refresh manager code from current business data
  const refreshManagerCode = () => {
    const resolvedBusinessId =
      businessId || toCanonicalBusinessId(getCurrentBusinessId({ allowFallback: false }));

    const preferred = resolveLocalManagerCode(resolvedBusinessId);
    if (preferred) {
      setManagerCode(preferred);
      kvSet('managerCode', preferred);

      const currentBusiness = kvGet('currentBusiness');
      if (
        currentBusiness &&
        typeof currentBusiness === 'object' &&
        !Array.isArray(currentBusiness) &&
        !currentBusiness.manager_code
      ) {
        kvSet('currentBusiness', { ...currentBusiness, manager_code: preferred });
      }

      return preferred;
    }

    // Fallback: currentBusiness / currentUser.business (legacy)
    let business = null;
    
    // First try currentBusiness (for backward compatibility)
    const currentBusiness = kvGet('currentBusiness');
    if (currentBusiness) {
      try {
        // getItem already handles JSON parsing, so it should return an object
        // But if it's a string, try to parse it
        if (typeof currentBusiness === 'string') {
          // Check if it's corrupted
          if (currentBusiness === '[object Object]' || currentBusiness === '"[object Object]"') {
            console.warn('⚠️ [ROLE CONTEXT] Corrupted currentBusiness data found, cleaning up');
            kvRemove('currentBusiness');
          } else {
            business = JSON.parse(currentBusiness);
          }
        } else {
          business = currentBusiness;
        }
      } catch (error) {
        console.error('Error parsing currentBusiness:', error);
        // Clean up corrupted data
        if (currentBusiness === '[object Object]' || currentBusiness === '"[object Object]"') {
          kvRemove('currentBusiness');
        }
      }
    }
    
    // If not found, try currentUser.business
    if (!business) {
      const currentUser = kvGet('currentUser');
      if (currentUser) {
        try {
          // getItem already handles JSON parsing
          const user = typeof currentUser === 'string' ? JSON.parse(currentUser) : currentUser;
          if (user && typeof user === 'object' && !Array.isArray(user)) {
            business = user.business;
          }
        } catch (error) {
          console.error('Error parsing currentUser:', error);
          // Clean up corrupted data
          if (currentUser === '[object Object]' || currentUser === '"[object Object]"') {
            kvRemove('currentUser');
          }
        }
      }
    }
    
    if (business && typeof business === 'object' && business.manager_code) {
      setManagerCode(business.manager_code);
      kvSet('managerCode', business.manager_code);
      return business.manager_code;
    }
    return null;
  };

  // Set manager code: local hash + push hash to Supabase (never plaintext).
  const setManagerCodeAndSave = async (code) => {
    try {
      // A shared PIN resolves to the worker first, locking the manager out of
      // CodeEntry — reject the collision instead of creating it (RL12).
      const collidingWorker = workers.find(
        (w) => w?.code != null && String(w.code) === String(code)
      );
      if (collidingWorker) {
        return {
          success: false,
          error: 'code_used_by_worker',
          workerName: collidingWorker.name
        };
      }

      const trimmed = String(code || '').trim();
      if (trimmed.length < 2) {
        return { success: false, error: 'code_too_short' };
      }
      const { offlineAuthService } = await import('../services/offlineAuthService');
      const hashed = await offlineAuthService.hashPassword(trimmed);

      // Session state keeps plaintext for UX; durable storage keeps PBKDF2 hash.
      setManagerCode(trimmed);
      kvSet('managerCode', hashed);

      try {
        const currentBusiness = kvGet('currentBusiness');
        let business = currentBusiness;
        if (typeof currentBusiness === 'string') {
          business = JSON.parse(currentBusiness);
        }
        if (business && typeof business === 'object') {
          const updatedBusiness = { ...business, manager_code: hashed };
          kvSet('currentBusiness', updatedBusiness);
          if (business.id) {
            offlineAuthService.updateBusiness(business.id, { manager_code: hashed });
          }
        }
      } catch (localErr) {
        console.warn('Local manager code persist warning:', localErr);
      }

      // Cloud: push PBKDF2 hash only (never plaintext). Enables manager code on
      // browser / second device after Pages login. Legacy plaintext rows are overwritten.
      try {
        const { supabase } = await import('../config/supabase');
        const currentBusiness = kvGet('currentBusiness');
        let business = currentBusiness;
        if (typeof currentBusiness === 'string') {
          business = JSON.parse(currentBusiness);
        }
        if (business?.id || business?.cloudId) {
          const { resolveSupabaseBusinessId } = await import('../utils/businessTypeCloud');
          const remoteId =
            resolveSupabaseBusinessId(business.id) ||
            (business.cloudId ? String(business.cloudId) : null) ||
            String(business.id);
          if (remoteId) {
            const { error } = await supabase
              .from('businesses')
              .update({ manager_code: hashed, updated_at: new Date().toISOString() })
              .eq('id', remoteId);
            if (error) {
              console.error('Error syncing manager code hash to Supabase:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error syncing manager code to Supabase:', error);
      }
      return { success: true };
    } catch (error) {
      console.error('Error setting manager code:', error);
      throw error;
    }
  };

  // Handle cash register modal actions
  const handleCashRegisterAction = async (result) => {
    if (!pendingCashRegisterAction) {
      throw new Error('No pending cash register action');
    }

    const { action, amount, cashAmount, electronicAmount, totalAmount, explanation, amountsByProvider } = result;
    const resolvedCashAmount = Number.isFinite(Number(cashAmount)) ? Number(cashAmount) : (Number(amount) || 0);
    const resolvedElectronicAmount = Number.isFinite(Number(electronicAmount)) ? Number(electronicAmount) : 0;
    const resolvedTotalAmount = Number.isFinite(Number(totalAmount))
      ? Number(totalAmount)
      : (resolvedCashAmount + resolvedElectronicAmount);
    
    if (pendingCashRegisterAction.type === 'login') {
      // Handle worker login cash confirmation
      if (action === 'confirm') {
        // Confirm the previous amount
        const { workerRole } = pendingCashRegisterAction;
        cashRegisterService.recordWorkerLogin(
          workerRole.id, 
          workerRole.name, 
          resolvedCashAmount, 
          businessId
        );
        try {
          await sessionRecorder.recordLogin(
            workerRole.id,
            workerRole.name,
            resolvedCashAmount,
            businessId,
            'confirm',
            resolvedElectronicAmount,
            amountsByProvider || null
          );
        } catch (e) {
          console.warn('[RoleContext] recordLogin failed:', e?.message || e);
        }
        // Login: no print step — close modal and open POS immediately.
        cashRegisterLoginCompletedRef.current = true;
        setShowCashRegisterModal(false);
        setPendingCashRegisterAction(null);
        setCurrentRole(workerRole);
        if (businessId) markRolePinUnlocked(businessId);
        emitCashierGreeting('welcome', { workerName: workerRole.name });
        navigateToDashboard(workerRole);
      } else if (action === 'reject') {
        // Reject and use new amount
        const { workerRole } = pendingCashRegisterAction;
        cashRegisterService.recordWorkerLogin(
          workerRole.id, 
          workerRole.name, 
          resolvedCashAmount, 
          businessId
        );
        // Record session
        try {
          await sessionRecorder.recordLogin(
            workerRole.id,
            workerRole.name,
            resolvedCashAmount,
            businessId,
            'reject',
            resolvedElectronicAmount,
            amountsByProvider || null
          );
        } catch (e) {
          console.warn('[RoleContext] recordLogin failed:', e?.message || e);
        }
        cashRegisterLoginCompletedRef.current = true;
        setShowCashRegisterModal(false);
        setPendingCashRegisterAction(null);
        setCurrentRole(workerRole);
        if (businessId) markRolePinUnlocked(businessId);
        emitCashierGreeting('welcome', { workerName: workerRole.name });
        navigateToDashboard(workerRole);
      }
    } else if (pendingCashRegisterAction.type === 'logout') {
      if (action === 'confirm' || action === 'reject') {
        const { workerRole } = pendingCashRegisterAction;
        await recordWorkerLogoutAndExit(workerRole, resolvedCashAmount, action, {
          electronicAmount: resolvedElectronicAmount,
          totalAmount: resolvedTotalAmount,
          amountsByProvider: amountsByProvider || null,
          explanation: explanation || ''
        }, { deferNavigation: true });
        emitCashierGreeting('farewell', { workerName: workerRole.name });
      }
    } else if (pendingCashRegisterAction.type === 'pause') {
      if (action === 'pause') {
        const { workerRole } = pendingCashRegisterAction;
        const pauseOk = sessionRecorder.recordPause(workerRole.id, businessId);
        if (!pauseOk) {
          throw new Error('pause_failed');
        }
        emitCashierGreeting('pause', { workerName: workerRole.name });
        allowRemoveRoleSaveRef.current = true;
        clearRolePinSession();
        setCurrentRole(null);
        if (businessId) {
          kvRemove(roleStorageKey(businessId));
        }
        setShowCashRegisterModal(false);
        setPendingCashRegisterAction(null);
        setShowCodeModal(true);
      }
    }
  };

  // Reset lockout manually (for admin use or successful main login)
  const resetLockout = useCallback(() => {
    lockoutService.resetLockout();
  }, []);

  // Get lockout status for display
  const getLockoutStatus = useCallback(() => {
    return lockoutService.getLockoutStatus();
  }, []);

  const hasPermission = useCallback(
    (permission) => hasPagePermission(currentRole, permission),
    [currentRole]
  );

  const handleCashRegisterPause = useCallback(() => {
    if (!currentRole) return;

    const activeSession = cashRegisterService.getActiveSession(businessId);
    if (!activeSession) {
      return;
    }

    setPendingCashRegisterAction({
      type: 'pause',
      workerRole: currentRole
    });
    setCashRegisterMode('pause');
    setShowCashRegisterModal(true);
  }, [currentRole, businessId]);

  // Stable session value — excludes PIN/caisse flags so modal open/close
  // does not re-render Layout children, keep-alives, or POS pages.
  const sessionValue = useMemo(
    () => ({
      currentRole,
      workers,
      managerCode,
      addWorker,
      removeWorker,
      toggleWorkerStatus,
      updateWorkerPermissions,
      loginWithCode,
      logoutFromRole,
      logoutToLoginPage,
      clearAllWorkers,
      hasPermission,
      setManagerCodeAndSave,
      generateWorkerCode,
      generateWorkerId,
      migrateWorkersToNewIdSystem,
      refreshManagerCode,
      businessId,
      ready,
      reloadWorkersForBusiness,
      resetLockout,
      getLockoutStatus
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: capture fns when session state changes
    [
      currentRole,
      workers,
      managerCode,
      businessId,
      ready,
      hasPermission,
      reloadWorkersForBusiness,
      resetLockout,
      getLockoutStatus
    ]
  );

  const modalValue = useMemo(
    () => ({
      showCodeModal,
      setShowCodeModal,
      editingWorker,
      setEditingWorker,
      showCashRegisterModal,
      setShowCashRegisterModal,
      cashRegisterMode,
      pendingCashRegisterAction,
      setPendingCashRegisterAction,
      handleCashRegisterAction,
      cancelCashRegisterModal,
      handleCashRegisterPause
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: capture fns when modal state changes
    [
      showCodeModal,
      editingWorker,
      showCashRegisterModal,
      cashRegisterMode,
      pendingCashRegisterAction,
      handleCashRegisterPause
    ]
  );

  return (
    <RoleSessionContext.Provider value={sessionValue}>
      <RoleModalContext.Provider value={modalValue}>
        {children}
      </RoleModalContext.Provider>
    </RoleSessionContext.Provider>
  );
};
